/**
 * Which component shows each artifact type in the workspace pane (contract reviews, projects, deliverables and
 * tasks open over the conversation instead: see overlays.ts). Viewers load lazily (the invoice editor
 * and the email thread are heavy) and can be swapped out (the design preview uses stand-ins with no
 * backend).
 */
import { defineAsyncComponent, inject, markRaw, type Component, type InjectionKey } from 'vue'
import type { ArtifactType } from '../../artifacts'

const DraftViewer = markRaw(defineAsyncComponent(() => import('./DraftViewer.vue')))
const ThreadViewer = markRaw(defineAsyncComponent(() => import('./ThreadViewer.vue')))
const InvoiceViewer = markRaw(defineAsyncComponent(() => import('./InvoiceViewer.vue')))
const RecordViewer = markRaw(defineAsyncComponent(() => import('./RecordViewer.vue')))
const BrowserViewer = markRaw(defineAsyncComponent(() => import('./BrowserViewer.vue')))
const PitchViewer = markRaw(defineAsyncComponent(() => import('./PitchViewer.vue')))
const FileViewer = markRaw(defineAsyncComponent(() => import('./FileViewer.vue')))
const ChangesViewer = markRaw(defineAsyncComponent(() => import('./ChangesViewer.vue')))

const viewers = new Map<ArtifactType | 'group', Component>([
    ['draft', DraftViewer],
    ['thread', ThreadViewer],
    ['invoice', InvoiceViewer],
    ['browser', BrowserViewer],
    ['outreach', PitchViewer],
    ['file', FileViewer],
    ['changes', ChangesViewer],
])

export function viewerFor(type: ArtifactType, group = false): Component {
    if (group) return viewers.get('group') ?? RecordViewer
    return viewers.get(type) ?? RecordViewer
}

/** Swap a viewer (previews, tests). */
export function registerArtifactViewer(type: ArtifactType | 'group', c: Component): void {
    viewers.set(type, markRaw(c))
}

/** What a viewer may tell the pane chrome: autosave state, a better title once loaded. */
export type ViewerStatus = 'idle' | 'saving' | 'saved' | 'error'

/** An `email_drafts` row: everything the inbox composer needs to edit and send it. */
export interface DraftRecord {
    id: string
    subject?: string
    body?: string
    to?: string[] | string
    cc?: string[] | string
    bcc?: string[] | string
    thread?: string | null
    sent_by?: string | null
    inbox?: string | null
    reply_to?: string | null
    references?: string | null
    attachments?: any[] | null
    signature?: string | null
    signature_disabled?: boolean | null
    signature_html?: string | null
    perspective?: string | null
    provider_id?: string | null
    provider_draft_id?: string | null
    agent_summary?: string | null
    variants?: import('../../api').DraftVariants | null
    [extra: string]: unknown
}

/** What a viewer saves back: the fields the composer lets the creator change (never the signature inside `body`). */
export type DraftPatch = Partial<Pick<DraftRecord, 'subject' | 'body' | 'to' | 'cc' | 'bcc' | 'attachments' | 'signature' | 'signature_disabled' | 'signature_html' | 'perspective'>>

/** Where drafts load from and save to. The app uses the `email_drafts` row the inbox composer reads. */
export interface DraftStore {
    load(id: string): Promise<DraftRecord | null>
    save(id: string, patch: DraftPatch): Promise<void>
    /** Delete the draft row (the mailbox copy is deleted separately, through the email API). */
    remove(id: string): Promise<void>
}

const supabaseDrafts: DraftStore = {
    async load(id) {
        // Support view (staff impersonation without email access): RLS hides drafts; don't ask.
        const { isSupportViewLimited } = await import('@/utils/supportView')
        if (isSupportViewLimited()) return null
        const { supabase } = await import('@/supabase')
        const { data } = await supabase.from('email_drafts').select('*').eq('id', id).maybeSingle()
        return (data as DraftRecord | null) ?? null
    },
    async save(id, patch) {
        const { supabase } = await import('@/supabase')
        // Who is writing it: teammates see "Alex is replying…" on the thread.
        const { data: session } = await supabase.auth.getSession()
        const composedBy = session?.session?.user?.id
        const { error } = await supabase.from('email_drafts')
            .update(composedBy ? { ...patch, composed_by: composedBy } : patch).eq('id', id)
        if (error) throw error
    },
    async remove(id) {
        const { supabase } = await import('@/supabase')
        const { error } = await supabase.from('email_drafts').delete().eq('id', id)
        if (error) throw error
    },
}

let drafts: DraftStore = supabaseDrafts
export function draftStore(): DraftStore {
    return drafts
}
/** Previews / tests: serve drafts from somewhere else. */
export function setDraftStore(store: DraftStore | null): void {
    drafts = store ?? supabaseDrafts
}

/**
 * What a viewer knows about the pane it's in. Phones present the pane as a sheet: viewers switch to their phone
 * layout and can ask for the top detent (an editor opening). Null outside a pane (an inline draft).
 */
export interface PaneContext {
    /** The pane is a phone sheet. */
    readonly sheet: boolean
    /** Grow the sheet to its top detent. A no-op elsewhere. */
    expand(): void
    /**
     * Ask before the creator takes record `key` off screen in a way that unmounts it (minimizing the sheet,
     * closing its tab, switching to the live browser). `fn` resolves false to stay (e.g. unsaved edits the
     * creator chose to keep). Returns the unregister function.
     */
    guardLeave(key: string, fn: LeaveGuard): () => void
}
export type LeaveGuard = () => boolean | Promise<boolean>

/**
 * The pane's leave guards. `allow(keys)` asks the guards of those records (all of them without `keys`), one
 * at a time, and stops at the first that says stay. It answers synchronously while every guard does (nothing
 * unsaved), so a dismiss with nothing to ask stays in the same tick as the gesture.
 */
export function createLeaveGuards() {
    const guards: { key: string; fn: LeaveGuard }[] = []
    return {
        add(key: string, fn: LeaveGuard): () => void {
            const g = { key, fn }
            guards.push(g)
            return () => {
                const at = guards.indexOf(g)
                if (at >= 0) guards.splice(at, 1)
            }
        },
        allow(keys?: readonly string[]): boolean | Promise<boolean> {
            const list = guards.filter((g) => !keys || keys.includes(g.key))
            const from = (i: number): boolean | Promise<boolean> => {
                for (; i < list.length; i++) {
                    const ok = list[i].fn()
                    if (typeof ok !== 'boolean') return Promise.resolve(ok).then((yes) => yes && from(i + 1))
                    if (!ok) return false
                }
                return true
            }
            return from(0)
        },
    }
}

export const PANE_KEY: InjectionKey<PaneContext> = Symbol('workspacePane')

export function usePane(): PaneContext | null {
    return inject(PANE_KEY, null)
}
